import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Guarda datos en localStorage con un timestamp
   * @param key - Clave para almacenar los datos
   * @param data - Datos a almacenar
   */
  public save<T>(key: string, data: T): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(key, JSON.stringify(data));
      localStorage.setItem(`${key}Timestamp`, Date.now().toString());
    } catch (error) {
      console.error(`Error saving to localStorage [${key}]:`, error);
    }
  }

  /**
   * Obtiene datos de localStorage validando su expiración
   * @param key - Clave de los datos a obtener
   * @param maxAgeMs - Tiempo máximo de validez en milisegundos (opcional)
   * @returns Los datos si existen y son válidos, null en caso contrario
   */
  public get<T>(key: string, maxAgeMs?: number): T | null {
    if (!this.isBrowser) return null;
    try {
      const timestamp = localStorage.getItem(`${key}Timestamp`);
      const dataJson = localStorage.getItem(key);

      if (!dataJson) {
        return null;
      }

      // Si hay timestamp y maxAge, validar la expiración
      if (timestamp && maxAgeMs) {
        const age = Date.now() - parseInt(timestamp, 10);
        if (age > maxAgeMs) {
          // Cache expirada, limpiar
          this.clear(key);
          return null;
        }
      }

      return JSON.parse(dataJson) as T;
    } catch (error) {
      console.error(`Error reading from localStorage [${key}]:`, error);
      this.clear(key);
      return null;
    }
  }

  /**
   * Elimina datos de localStorage junto con su timestamp
   * @param key - Clave de los datos a eliminar
   */
  public clear(key: string): void {
    if (!this.isBrowser) return;
    try {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}Timestamp`);
    } catch (error) {
      console.error(`Error clearing localStorage [${key}]:`, error);
    }
  }

  /**
   * Verifica si existe una clave en localStorage
   * @param key - Clave a verificar
   * @returns true si existe, false en caso contrario
   */
  public has(key: string): boolean {
    if (!this.isBrowser) return false;
    return localStorage.getItem(key) !== null;
  }

  /**
   * Limpia todos los datos de localStorage
   */
  public clearAll(): void {
    if (!this.isBrowser) return;
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing all localStorage:', error);
    }
  }
}
